import type { EChartsOption } from "echarts";
import { useContext, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Container, Slider } from "@mui/material";
import DashboardCardHeader from "./DashboardCardHeader";
import { RopChart } from "../../../main/components/RopChart";
import { DashboardContext } from "../context/DashboardContexProvider";
import {
  getHourAndMinutes,
  getYearDay,
} from "../../../main/themes/defaultLayout";
import {
  alignSeriesValuesByDateTime,
  getChartHourLabel,
  getInitialSliderRange,
  mapSeriesValues,
  mapSeriesXLabels,
} from "../helpers/chart-time-alignment";
import {
  BaseErrorCard,
  BaseUsinaValue,
  PlantaDataEvolucao,
  getSelectedArea,
} from "../models/dados-indicadores.model";
import { EmptyTable } from "../../clients/components/EmptyState";
import { CardTypesEnum, EquipmentFilter } from "../types";
import { getCardIsLoading } from "./TextCardContent";

export type EvolutionChartValueAndDateTime = {
  dateTime: string;
  value: number;
};

export type EvolutionChartData = {
  evolution?: BaseUsinaValue[];
  irradiancia?: BaseUsinaValue[];
  potenciaAtivaAtMinutes?: BaseUsinaValue[];
  irradianciaMediaAtMinutes?: BaseUsinaValue[];
  planta?: PlantaDataEvolucao;
  cardError: BaseErrorCard;
};

export type EvolutionChartProps = {
  cardId?: string;
  title?: string;
  lastUpdate: Date;
  cardType: number;
  data: EvolutionChartData;
};

export type EvolutionChartContentProps = {
  data: EvolutionChartData;
  equipmentFilter: EquipmentFilter;
  showPotenciaAtivaInCharts: boolean;
  evolucaoColor?: string;
  irradianciaColor?: string;
  startWithFullRange?: boolean;
  isLoading: boolean,
  elementRef?: React.RefObject<HTMLDivElement>;
};

function getDisplayableData(
  plantaData: any,
  data: EvolutionChartData,
  equipmentFilter: EquipmentFilter
) {
  if ((equipmentFilter.ugName || equipmentFilter.inversorName) && plantaData) {
    return {
      evolution: plantaData.values.evolution,
      irradiancia: data.irradiancia,
      potenciaAtivaAtMinutes: plantaData.values.potenciaAtivaAtMinutes,
      irradianciaMediaAtMinutes: data.irradianciaMediaAtMinutes,
    };
  }
  return data;
}

export function EvolutionChartContent({
  data,
  equipmentFilter,
  showPotenciaAtivaInCharts,
  evolucaoColor: propEvolucaoColor,
  irradianciaColor: propIrradianciaColor,
  startWithFullRange,
  isLoading,
  elementRef,
}: EvolutionChartContentProps) {
  const { cardsDisplayableColorProps } = useContext(DashboardContext);

  const evolucaoColor = propEvolucaoColor ?? cardsDisplayableColorProps.evolucaoGeracao.evolucaoColor;
  const irradianciaColor = propIrradianciaColor ?? cardsDisplayableColorProps.evolucaoGeracao.irradianciaColor;

  const plantaData =
    data.planta && getSelectedArea(data.planta, equipmentFilter);

  const displayableData = getDisplayableData(plantaData, data, equipmentFilter);

  const potenciaAtivaSeriesKey =
    displayableData.potenciaAtivaAtMinutes
      ?.map((item: BaseUsinaValue) => item.dateTime)
      .join("|") ?? "";

  const [potenciaAtivaXLabels, setPotenciaAtivaXLabels] = useState(() =>
    mapSeriesXLabels(displayableData.potenciaAtivaAtMinutes)
  );

  const xLabels = !showPotenciaAtivaInCharts
    ? displayableData.evolution?.map((item: BaseUsinaValue) =>
      getHourAndMinutes(item.dateTime ?? "")
    )
    : potenciaAtivaXLabels;
  const evolutionValues = mapSeriesValues(displayableData.evolution);
  const filterIrradiance = displayableData.irradiancia?.filter((item: BaseUsinaValue) =>
    displayableData.evolution?.some((e: BaseUsinaValue) => e.dateTime === item.dateTime)
  );
  const irradianceValues = mapSeriesValues(filterIrradiance);

  const [potenciaAtivaAtMinutesValues, setPotenciaAtivaAtMinutesValues] =
    useState(() => mapSeriesValues(displayableData.potenciaAtivaAtMinutes));

  const [irradianceAtMinutes, setIrradianceAtMinutes] = useState(() =>
    alignSeriesValuesByDateTime(
      displayableData.potenciaAtivaAtMinutes,
      displayableData.irradianciaMediaAtMinutes
    )
  );

  const legendData = !showPotenciaAtivaInCharts
    ? ["Energia Gerada (MWh)", "Irradiância Média (Wh/m2)"]
    : ["Potência Ativa (MW)", "Irradiância (W/m2)"];
  const potenciaAtivaDay =
    displayableData.potenciaAtivaAtMinutes &&
      displayableData.potenciaAtivaAtMinutes.length > 0
      ? getYearDay(displayableData.potenciaAtivaAtMinutes[0].dateTime ?? "")
      : "";
  const evolutionDay =
    displayableData.evolution && displayableData.evolution.length > 0
      ? getYearDay(displayableData.evolution[0].dateTime ?? "")
      : "";
  const option: EChartsOption = {
    title: {
      text: !showPotenciaAtivaInCharts ? evolutionDay : potenciaAtivaDay,
      textStyle: {
        fontSize: 14,
        fontWeight: "bold",
      },
    },
    backgroundColor: "transparent",
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "cross",
        crossStyle: {
          color: "#999",
        },
      },
      confine: true,
    },
    toolbox: {
      // feature: {
      //   dataView: { show: true, readOnly: false },
      //   magicType: { show: true, type: ["line", "bar"] },
      //   restore: { show: true },
      //   saveAsImage: { show: true },
      // },
    },
    legend: {
      data: legendData,
      left: "right",
    },
    xAxis: [
      {
        type: "category",
        data: xLabels,
        axisPointer: {
          type: "shadow",
        },
      },
    ],
    yAxis: !showPotenciaAtivaInCharts
      ? [
        {
          type: "value",
          name: "MWh",
        },
        {
          type: "value",
          name: "(Wh/m2)",
        },
      ]
      : [
        {
          type: "value",
          name: "(MW)",
        },
        {
          type: "value",
          name: "(W/m2)",
        },
      ],
    series: !showPotenciaAtivaInCharts
      ? [
        {
          name: "Energia Gerada (MWh)",
          type: "bar",
          itemStyle: {
            color: evolucaoColor,
          },
          tooltip: {
            valueFormatter: function (value) {
              let vPtBr: string;
              if (typeof value !== "number") {
                vPtBr = `${value}`;
              } else {
                vPtBr = value.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              }
              return `${vPtBr} MWh`;
            },
          },
          data: evolutionValues,
        },
        {
          name: "Irradiância Média (Wh/m2)",
          yAxisIndex: 1,
          itemStyle: {
            color: irradianciaColor,
          },
          type: "line",
          areaStyle: {
            opacity: 0.3
          },
          tooltip: {
            valueFormatter: function (value) {
              let vPtBr: string;
              if (typeof value !== "number") {
                vPtBr = `${value}`;
              } else {
                vPtBr = value.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              }
              return `${vPtBr} W/m2`;
            },
          },
          data: irradianceValues,
        },
      ]
      : [
        {
          name: "Potência Ativa (MW)",
          type: "line",
          yAxisIndex: 0,
          itemStyle: {
            color: evolucaoColor,
          },
          tooltip: {
            valueFormatter: function (value) {
              let vPtBr: string;
              if (typeof value !== "number") {
                vPtBr = `${value}`;
              } else {
                vPtBr = value.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              }
              return `${vPtBr} MW`;
            },
          },
          data: potenciaAtivaAtMinutesValues,
        },
        {
          name: "Irradiância (W/m2)",
          yAxisIndex: 1,
          itemStyle: {
            color: irradianciaColor,
          },
          type: "line",
          tooltip: {
            valueFormatter: function (value) {
              let vPtBr: string;
              if (typeof value !== "number") {
                vPtBr = `${value}`;
              } else {
                vPtBr = value.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              }
              return `${vPtBr} W/m2`;
            },
          },
          data: irradianceAtMinutes,
        },
      ],
  };

  const minDistance = 10;

  const maxSliderValue =
    displayableData.potenciaAtivaAtMinutes?.length ?? 0;

  const [sliderValue, setSliderValue] = useState<number[]>(() =>
    getInitialSliderRange(maxSliderValue, startWithFullRange)
  );

  useEffect(() => {
    setSliderValue(getInitialSliderRange(maxSliderValue, startWithFullRange));
  }, [
    maxSliderValue,
    startWithFullRange,
    equipmentFilter.ugName,
    equipmentFilter.inversorName,
    potenciaAtivaSeriesKey,
  ]);

  const handleChangeSlider = (
    _event: Event,
    newValue: number | number[],
    activeThumb: number,
  ) => {
    if (!Array.isArray(newValue)) {
      return;
    }

    if (activeThumb === 0) {
      setSliderValue([Math.min(newValue[0], sliderValue[1] - minDistance), sliderValue[1]]);
    } else {
      setSliderValue([sliderValue[0], Math.max(newValue[1], sliderValue[0] + minDistance)]);
    }
  };

  function valuetext(index: number) {
    const currentPotenciaAtiva = displayableData.potenciaAtivaAtMinutes?.[index - 1] as BaseUsinaValue
    if (currentPotenciaAtiva)
      return getChartHourLabel(currentPotenciaAtiva.dateTime);
    return `${index}`
  }

  useEffect(() => {
    const potenciaAtivaAtMinutes = displayableData.potenciaAtivaAtMinutes as
      | BaseUsinaValue[]
      | undefined
    if (!potenciaAtivaAtMinutes?.length) {
      setIrradianceAtMinutes([])
      setPotenciaAtivaAtMinutesValues([])
      setPotenciaAtivaXLabels([])
      return
    }

    const len = potenciaAtivaAtMinutes.length
    const rawMin = sliderValue[0] - 1
    const rawMax = sliderValue[1] - 1
    const minIndex = Math.max(0, Math.min(rawMin, len - 1))
    const maxIndex = Math.max(minIndex, Math.min(rawMax, len - 1))

    const alignedIrradianceAtMinutes = alignSeriesValuesByDateTime(
      potenciaAtivaAtMinutes,
      displayableData.irradianciaMediaAtMinutes
    );
    const filteredIrradianciaMediaAtMinutes = alignedIrradianceAtMinutes.filter(
      (_item, i) => i >= minIndex && i <= maxIndex
    );
    const filteredPotenciaAtivaAtMinutes = potenciaAtivaAtMinutes.filter(
      (_item, i) => i >= minIndex && i <= maxIndex
    );
    const xLabels = mapSeriesXLabels(potenciaAtivaAtMinutes);
    const filteredXLabels = xLabels.filter(
      (_item, i) => i >= minIndex && i <= maxIndex
    );

    setIrradianceAtMinutes(filteredIrradianciaMediaAtMinutes);
    setPotenciaAtivaAtMinutesValues(
      mapSeriesValues(filteredPotenciaAtivaAtMinutes)
    );
    setPotenciaAtivaXLabels(filteredXLabels);
  }, [
    sliderValue,
    displayableData.potenciaAtivaAtMinutes,
    displayableData.irradianciaMediaAtMinutes,
  ]);

  const hasData =
    (displayableData.evolution?.length ?? 0) > 0 ||
    (displayableData.irradiancia?.length ?? 0) > 0 ||
    (displayableData.irradianciaMediaAtMinutes?.length ?? 0) > 0 ||
    (displayableData.potenciaAtivaAtMinutes?.length ?? 0) > 0;

  return (
    <>
      {!isLoading ? (
        hasData ? (
          <>
            <Container
              ref={elementRef}
              sx={{
                display: "flex",
                flexDirection: "column",
                height: showPotenciaAtivaInCharts ? "370px" : "90%",
                width: "100%",
              }}
            >
              <RopChart option={option} />
            </Container>
            {showPotenciaAtivaInCharts && maxSliderValue > 0 &&
              <Box>
                <Slider
                  sx={() => ({
                    width: "180px",
                    height: 15,
                    '& .MuiSlider-thumb': {
                      width: 6,
                      height: 25,
                      borderRadius: 2
                    },
                    '& .MuiSlider-rail': {
                      opacity: 0.28,
                    }
                  })}
                  min={1}
                  max={maxSliderValue}
                  valueLabelFormat={valuetext}
                  value={sliderValue}
                  onChange={handleChangeSlider}
                  valueLabelDisplay="auto"
                  disableSwap
                /></Box>}</>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <EmptyTable title="Nenhum dado encontrado!" />
          </Box>
        )
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </>
  )
}

export default function EvolutionChart({
  cardId,
  data,
  title,
  lastUpdate,
  cardType,
}: EvolutionChartProps) {
  const timeout = 120;
  const now = new Date();
  const delay = lastUpdate ? (now.getTime() - lastUpdate.getTime()) / 1000 : 0;
  const hasDelay = delay > timeout;
  const {
    showPotenciaAtivaInCharts,
    setShowPotenciaAtivaInCharts,
    cardsDisplayableColorProps,
    setCardsDisplayableColorProps,
    equipmentFilter,
    cardIsLoading,
    requestParamCards,
  } = useContext(DashboardContext);

  const [evolucaoColor, setEvolucaoColor] = useState<string>(
    cardsDisplayableColorProps.evolucaoGeracao.evolucaoColor
  );
  const [irradianciaColor, setIrradianciaColor] = useState<string>(
    cardsDisplayableColorProps.evolucaoGeracao.irradianciaColor
  );

  const [isMouseIn, setIsMouseIn] = useState(false);

  const [openModal, setopenModal] = useState<boolean>(false);

  const elementRef = useRef(null);
  const headerTitle = title ? "Evolução da Geração" : "";

  const handleConfirmModal = () => {
    setCardsDisplayableColorProps({
      ...cardsDisplayableColorProps,
      evolucaoGeracao: {
        evolucaoColor: evolucaoColor,
        irradianciaColor: irradianciaColor,
      },
    });
    setopenModal(false);
  };

  return (
    <>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
        onMouseOver={() => setIsMouseIn(true)}
        onMouseLeave={() => setIsMouseIn(false)}
      >
        <DashboardCardHeader
          cardError={data.cardError}
          cardId={cardId}
          elementRef={elementRef}
          isMouseIn={isMouseIn}
          title={headerTitle}
          lastUpdate={lastUpdate}
          hasDelay={hasDelay}
          titleFontSize={21}
          openColorsModal={openModal}
          setOpenColorsModal={setopenModal}
          colorButton
          onConfirmColorsModal={handleConfirmModal}
          hasEquipmentFilter
          colorsOptions={[
            {
              title: !showPotenciaAtivaInCharts
                ? "Energia Gerada (MWh)"
                : "Potência Ativa (MW)",
              value: evolucaoColor,
              setValue: setEvolucaoColor,
            },
            {
              title: !showPotenciaAtivaInCharts
                ? "Irradiaância (W/m2)"
                : "Irradiância (W/m2)",
              value: irradianciaColor,
              setValue: setIrradianciaColor,
            },
          ]}
          toggleItems={{
            disabledLabel: "Energia Gerada",
            enabledLabel: "Potência Ativa",
            toggleValue: showPotenciaAtivaInCharts,
            setToggleValue: setShowPotenciaAtivaInCharts,
          }}
          hideExportImage
          cardType={CardTypesEnum.EvolucaoGeracao}
          hasFilter
        />
        <EvolutionChartContent
          data={data}
          equipmentFilter={equipmentFilter}
          showPotenciaAtivaInCharts={showPotenciaAtivaInCharts}
          evolucaoColor={evolucaoColor}
          irradianciaColor={irradianciaColor}
          isLoading={getCardIsLoading(cardType, cardIsLoading, requestParamCards)}
          elementRef={elementRef}
        />
      </Box>
    </>
  );
}
